import { useEffect, useMemo, useState, type FC } from "react";
import { Button, Modal } from "@heroui/react";
import type { HelpOrderDetail, HelpOrderStatus } from "../types";
import {
  HELP_STATUS_LABEL,
  HELP_NOTIFY_STATUS_OPTIONS,
  NOTIFY_TEMPLATES,
} from "../constants";

interface HelpNotifyModalProps {
  isOpen: boolean;
  order: HelpOrderDetail | null;
  onClose: () => void;
  onSend: (text: string) => void;
  isSending?: boolean;
}

function fillTemplate(status: HelpOrderStatus, etaMinutes: number): string {
  return NOTIFY_TEMPLATES[status].replace("{eta}", String(etaMinutes));
}

const HelpNotifyModal: FC<HelpNotifyModalProps> = ({
  isOpen,
  order,
  onClose,
  onSend,
  isSending = false,
}) => {
  const [selected, setSelected] = useState<HelpOrderStatus | "custom">(
    "custom",
  );
  const [text, setText] = useState("");

  const eta = order?.executor?.etaMinutes ?? 10;

  const variants = useMemo(
    () =>
      HELP_NOTIFY_STATUS_OPTIONS.map((status) => ({
        id: status,
        title: HELP_STATUS_LABEL[status],
        text: fillTemplate(status, eta),
      })),
    [eta],
  );

  useEffect(() => {
    if (!isOpen || !order) return;
    setSelected(order.status);
    setText(fillTemplate(order.status, eta));
  }, [isOpen, order, eta]);

  const handleSelect = (status: HelpOrderStatus | "custom") => {
    if (status === "custom") {
      setSelected("custom");
      return;
    }
    setSelected(status);
    setText(fillTemplate(status, eta));
  };

  return (
    <Modal.Backdrop
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      className="help-notify-modal-backdrop"
    >
      <Modal.Container>
        <Modal.Dialog className="help-notify-modal" data-testid="help-notify-modal">
          <Modal.CloseTrigger data-testid="help-notify-modal-close" />
          <Modal.Header>
            <Modal.Heading>Уведомление клиенту</Modal.Heading>
          </Modal.Header>
          <Modal.Body className="help-notify-modal__body">
            <p className="help-notify-modal__lead">
              Выберите заготовку или напишите свой текст. В этой версии
              сообщение сохраняется в истории заявки, push клиенту не
              отправляется.
            </p>
            <div className="help-notify-variants" role="listbox" aria-label="Шаблоны">
              {variants.map((variant) => (
                <button
                  key={variant.id}
                  type="button"
                  className={
                    selected === variant.id
                      ? "help-notify-variant help-notify-variant--active"
                      : "help-notify-variant"
                  }
                  onClick={() => handleSelect(variant.id)}
                >
                  <strong>{variant.title}</strong>
                  <span>{variant.text}</span>
                </button>
              ))}
              <button
                type="button"
                className={
                  selected === "custom"
                    ? "help-notify-variant help-notify-variant--active"
                    : "help-notify-variant"
                }
                onClick={() => handleSelect("custom")}
              >
                <strong>Свой текст</strong>
                <span>Напишите уведомление вручную</span>
              </button>
            </div>
            <div className="help-notify-modal__composer">
              <textarea
                className={
                  selected === "custom"
                    ? "help-notify-modal__textarea"
                    : "help-notify-modal__textarea help-notify-modal__textarea--from-template"
                }
                rows={3}
                value={text}
                onChange={(event) => {
                  setText(event.target.value);
                  setSelected("custom");
                }}
                placeholder="Текст уведомления"
                aria-label="Текст уведомления"
              />
            </div>
          </Modal.Body>
          <Modal.Footer className="help-notify-modal__footer">
            <Button
              variant="tertiary"
              onPress={onClose}
              isDisabled={isSending}
              data-testid="help-notify-cancel"
            >
              Отмена
            </Button>
            <Button
              variant="primary"
              isDisabled={!text.trim() || isSending}
              data-testid="help-notify-send"
              onPress={() => {
                if (!text.trim() || isSending) return;
                onSend(text.trim());
              }}
            >
              {isSending ? "Отправка..." : "Отправить"}
            </Button>
          </Modal.Footer>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
};

export default HelpNotifyModal;
